// eslint-disable-next-line import/no-deprecated
import {guidToInt} from '@wix/wixstores-client-storefront-sdk';
import {LineItemModel} from '../LineItems/LineItem.model';
import {CouponModel} from './Coupon.model';
import {PriceModel} from '../Price.model';
import {CartDiscount, DiscountRule, LineItem, MultiCurrencyPrice} from '@wix/auto_sdk_ecom_current-cart';
import {IOptionsSelectionsValue, IRenderingConfig, ModelContext, CartWithPrivateFields} from '../../../types/app.types';
import {getUniques} from '../../utils/getUniques';
import {SPECS} from '../../specs';
import {BusinessInfoModel} from './BusinessInfo.model';
import {CustomerInfoModel} from './CustomerInfo.model';
import {PaymentInfoModel} from './PaymentInfo.model';
import {DeliveryInfoModel} from './DeliveryInfo.model';

type CartDiscountWithPrivateFields = CartDiscount & {discountRule?: DiscountRule; lineItemIds: string[]};
export type LineItemWithPrivateFields = LineItem & {
  lineItemPrice: MultiCurrencyPrice;
};

export class CartModel {
  public id: string;
  public lineItems: LineItemModel[];
  public subtotal: PriceModel;
  public note: string;
  public businessInfo: BusinessInfoModel;
  public customerInfo: CustomerInfoModel;
  public paymentInfo: PaymentInfoModel;
  public purchaseFlowId: string;
  public deliveryInfo: DeliveryInfoModel;

  /**
   * @deprecated
   */
  public subtotalAfterDiscounts: PriceModel;
  /**
   * @deprecated
   */
  public discount?: PriceModel;

  public lineItemsTypes: string[];
  public hasSubscriptionItem: boolean;
  public hasItemWithFreeTrial: boolean;

  public coupon?: CouponModel;

  /**
   * @deprecated
   */
  public checkoutId: string;

  constructor(params: CartModel) {
    this.id = params.id;
    this.lineItems = params.lineItems;
    this.lineItemsTypes = params.lineItemsTypes;
    this.subtotal = params.subtotal;
    this.note = params.note;
    this.businessInfo = params.businessInfo;
    this.customerInfo = params.customerInfo;
    this.paymentInfo = params.paymentInfo;
    this.deliveryInfo = params.deliveryInfo;
    this.checkoutId = params.checkoutId;
    this.coupon = params.coupon;
    this.purchaseFlowId = params.purchaseFlowId;
    this.subtotalAfterDiscounts = params.subtotalAfterDiscounts;
    this.discount = params.discount;
    this.hasSubscriptionItem = params.hasSubscriptionItem;
    this.hasItemWithFreeTrial = params.hasItemWithFreeTrial;
  }

  public static fromCartV1(
    cart: CartWithPrivateFields,
    renderingConfigMap: Record<string, IRenderingConfig> | undefined,
    bookingsOptionsMap: Record<string, IOptionsSelectionsValue[]> | undefined,
    modelContext: ModelContext
  ): CartModel {
    const lineItemIdToRules = mapLineItemIdToRules(cart.appliedDiscounts as CartDiscountWithPrivateFields[]);
    const appliedCoupon = cart.appliedDiscounts?.find((discount) => discount?.coupon);
    const discountType = appliedCoupon?.discountType;

    const modeledLineItems = cart.lineItems.map((lineItem: LineItem) =>
      LineItemModel.fromCartV1(
        lineItem as LineItemWithPrivateFields,
        lineItemIdToRules[lineItem._id],
        modelContext,
        // eslint-disable-next-line import/no-deprecated
        renderingConfigMap?.[guidToInt(lineItem._id)],
        // eslint-disable-next-line import/no-deprecated
        bookingsOptionsMap?.[guidToInt(lineItem._id)]
      )
    );

    return new CartModel({
      id: cart._id,
      lineItems: modeledLineItems,
      lineItemsTypes: modelContext.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings)
        ? getUniques(modeledLineItems.map(({attributes}) => attributes.itemType))
        : [],
      subtotal: PriceModel.fromCartV1(cart.subtotal, modelContext),
      note: cart.buyerNote,
      businessInfo: BusinessInfoModel.fromCartV1(cart.currency),
      customerInfo: CustomerInfoModel.fromCartV1(cart.conversionCurrency),
      paymentInfo: PaymentInfoModel.fromCartV1(cart.paymentCurrency),
      checkoutId: cart.checkoutId,
      coupon: appliedCoupon ? CouponModel.fromCartV1(appliedCoupon?.coupon, discountType, modelContext) : undefined,
      purchaseFlowId: cart.purchaseFlowId,
      deliveryInfo: DeliveryInfoModel.fromCartV1(cart.contactInfo?.address),
      subtotalAfterDiscounts: modelContext.viewerContext.isExperimentEnabled(SPECS.RefactorCouponOnCart)
        ? undefined
        : PriceModel.fromCartV1(cart.subtotalAfterDiscounts, modelContext),
      discount: modelContext.viewerContext.isExperimentEnabled(SPECS.RefactorCouponOnCart)
        ? undefined
        : PriceModel.fromCartV1(cart.discount, modelContext),
      hasSubscriptionItem: cart.lineItems.some((lineItem) => lineItem.subscriptionOptionInfo),
      hasItemWithFreeTrial: cart.lineItems.some(
        (lineItem) => !!lineItem.subscriptionOptionInfo?.subscriptionSettings?.freeTrialPeriod
      ),
    });
  }
}

function mapLineItemIdToRules(appliedDiscounts: CartDiscountWithPrivateFields[]): Record<string, string[]> {
  if (!appliedDiscounts) {
    return {};
  }
  return appliedDiscounts.reduce((acc, discount) => {
    if (!discount.discountRule) {
      return acc;
    }
    discount.lineItemIds?.forEach((lineItemId) => {
      acc[lineItemId] = acc[lineItemId] ?? [];
      acc[lineItemId].push(discount.discountRule.name.translated);
    });
    return acc;
  }, {});
}
